import type { ContentLocale, ContentStatus, ContentTypeName } from "@content-types"
import type { ContentDocumentDetail, ContentDocumentListPage } from "./content-server"

export const DASHBOARD_CONTENT_LIST_PER_PAGE = 50
export const DASHBOARD_CONTENT_LIST_PAGE_SIZE_OPTIONS = [25, 50, 100] as const
export const DASHBOARD_CONTENT_LIST_STALE_MS = 60 * 1000
export const DASHBOARD_CONTENT_DETAIL_STALE_MS = 60 * 1000
export type ContentLanguageFilter = "all" | ContentLocale
export type ContentTeamFilter = "all" | string

type ContentListQueryInput = {
  fetchDocuments: (input: {
    language?: ContentLocale
    page: number
    perPage: number
    query: string
    status: ContentStatus
    teamKey?: string
    type: ContentTypeName
  }) => Promise<ContentDocumentListPage>
  language: ContentLanguageFilter
  page: number
  pageSize: number
  query: string
  status: ContentStatus
  team: ContentTeamFilter
  type: ContentTypeName
}

export function contentListQueryKey({
  language,
  page,
  perPage = DASHBOARD_CONTENT_LIST_PER_PAGE,
  query,
  status,
  team,
  type,
}: {
  language: ContentLanguageFilter
  page: number
  perPage?: number
  query: string
  status: ContentStatus
  team: ContentTeamFilter
  type: ContentTypeName
}) {
  return ["dashboard-content-documents", type, status, language, team, query, page, perPage] as const
}

/**
 * The content type a list key describes, or null if it is not a list key.
 *
 * Reads the position `contentListQueryKey` writes the type into, so the two
 * stay together: change the key's shape and this is the other half to change.
 */
function contentTypeFromListQueryKey(queryKey: readonly unknown[] | undefined): ContentTypeName | null {
  if (!queryKey || queryKey[0] !== "dashboard-content-documents") return null
  const type = queryKey[1]
  return typeof type === "string" ? (type as ContentTypeName) : null
}

export function contentListTypeQueryKey(type: ContentTypeName) {
  return ["dashboard-content-documents", type] as const
}

export function contentDocumentQueryKey(id: string) {
  return ["dashboard-content-document", id] as const
}

// Shared by the list's team filter and the editor's team field, so a set of
// teams fetched for one is fresh for the other.
export function careerTeamsQueryKey() {
  return ["dashboard-career-teams"] as const
}

export function contentDocumentQueryOptions({
  fetchDocument,
  id,
}: {
  fetchDocument: (id: string) => Promise<ContentDocumentDetail>
  id: string
}) {
  return {
    queryFn: () => fetchDocument(id),
    queryKey: contentDocumentQueryKey(id),
    staleTime: DASHBOARD_CONTENT_DETAIL_STALE_MS,
    /*
     * Terminate rather than pause. The default "online" network mode holds a
     * failing fetch in `paused` whenever the online manager doubts the
     * connection, which left a deep link to a missing record on an eternal
     * loading screen instead of reaching the editor's load-failure handling.
     * Whichever caller starts the fetch — the route loader's ensureQueryData
     * or the editor's subscription — its options govern the retryer, so the
     * policy lives here, once.
     *
     * The retry count stays at the library's default three: the editor
     * navigates away when this query settles into error, so a single
     * transient failure (a Lambda cold start, a fetch aborted by a racing
     * navigation) must not be enough to bounce a reader out of the editor.
     */
    networkMode: "always" as const,
    retry: 3,
  }
}

export function contentListQueryOptions({
  fetchDocuments,
  language,
  page,
  pageSize,
  query,
  status,
  team,
  type,
}: ContentListQueryInput) {
  return {
    /*
     * Hold the previous rows while paging, filtering or searching — but only
     * within one content type.
     *
     * All of those change the key, and without this the table emptied to
     * loading rows on every one of them: five skeleton rows where fifty
     * records had been, then fifty again a moment later. The page grew and
     * collapsed around the swap, which is the shift you feel while typing in
     * the search box. Holding the previous page means the rows stay put and
     * only their contents change.
     *
     * Changing section is not that. It used to look like it, because the route
     * awaited its list before the section could paint, so the rows on screen
     * were always the ones being asked for. Now that a tab change commits
     * immediately, keeping the previous page unconditionally left news
     * articles sitting under the Careers heading, in careers columns, until
     * the fetch returned — a table that is not merely stale but describing the
     * wrong records. So the previous data is kept only when it came from the
     * same type, and a section change falls through to the skeleton, which is
     * what a table with nothing to show yet should look like.
     */
    placeholderData: (
      previousData: ContentDocumentListPage | undefined,
      previousQuery?: { queryKey: readonly unknown[] }
    ) => (contentTypeFromListQueryKey(previousQuery?.queryKey) === type ? previousData : undefined),
    queryFn: () =>
      fetchDocuments({
        language: language === "all" ? undefined : language,
        page,
        perPage: pageSize,
        query,
        status,
        teamKey: team === "all" ? undefined : team,
        type,
      }),
    queryKey: contentListQueryKey({ language, page, perPage: pageSize, query, status, team, type }),
    staleTime: DASHBOARD_CONTENT_LIST_STALE_MS,
  }
}
